import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { TicketStatus, TicketPriority } from '@/types/ticket';
// @ts-ignore
import { ProjectStats } from '@/types/project';
import { UserRole } from '@/types/user';
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  PROJECT_STATUS_COLORS,
  ROLE_COLORS,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
} from './constants';

export function formatDate(date: string | Date, formatStr: string = DATE_FORMAT): string {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? format(dateObj, formatStr) : 'Invalid date';
  } catch (error) {
    return 'Invalid date';
  }
}

export function formatDateTime(date: string | Date): string {
  return formatDate(date, DATE_TIME_FORMAT);
}

export function formatRelativeTime(date: string | Date): string {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? formatDistanceToNow(dateObj, { addSuffix: true }) : 'Invalid date';
  } catch (error) {
    return 'Invalid date';
  }
}

export function formatStatus(status: TicketStatus): string {
  const statusMap: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    waiting: 'Waiting',
    resolved: 'Resolved',
    closed: 'Closed',
  };
  return statusMap[status] || status;
}

export function formatPriority(priority: TicketPriority): string {
  const priorityMap: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  };
  return priorityMap[priority] || priority;
}

export function formatstring(status: string): string {
  const statusMap: Record<string, string> = {
    active: 'Active',
    on_hold: 'On Hold',
    archived: 'Archived',
  };
  return statusMap[status] || status;
}

export function formatRole(role: UserRole): string {
  const roleMap: Record<UserRole, string> = {
    admin: 'Admin',
    agent: 'Agent',
    customer: 'Customer',
  };
  return roleMap[role] || role;
}

export function getStatusColor(status: TicketStatus): string {
  return STATUS_COLORS[status] || 'gray';
}

export function getPriorityColor(priority: TicketPriority): string {
  return PRIORITY_COLORS[priority] || 'gray';
}

export function getstringColor(status: string): string {
  return PROJECT_STATUS_COLORS[status] || 'gray';
}

export function getRoleColor(role: UserRole): string {
  return ROLE_COLORS[role] || 'gray';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat().format(num);
}

export function formatPercentage(num: number, decimals: number = 1): string {
  return `${num.toFixed(decimals)}%`;
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.substring(0, length) + '...';
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}
